'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  type TenantFeatureFlags,
  DEFAULT_TENANT_FEATURE_FLAGS,
} from '@terra/shared-contracts-feature-flags';
import { useUserTenants } from '../../hooks/api/useUserTenants';
import { useTenantFeatureFlags } from '../../hooks/api/useTenantFeatureFlags';
import { useAuth } from './auth/useAuth';

type FeatureFlagsContextValue = {
  featureFlags: TenantFeatureFlags;
  isLoading: boolean;
  isFetched: boolean;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { currentTenantId } = useUserTenants({
    enabled: isAuthenticated && !isAuthLoading,
  });
  const { featureFlags, isLoading, isFetched } = useTenantFeatureFlags({
    tenantId: currentTenantId ?? undefined,
  });

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      featureFlags: (featureFlags ??
        DEFAULT_TENANT_FEATURE_FLAGS) as TenantFeatureFlags,
      isLoading,
      isFetched,
    }),
    [featureFlags, isFetched, isLoading],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlagsContext = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagsContext must be used within FeatureFlagsProvider',
    );
  }
  return context;
};
